import type { ReactNode } from 'react';
import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { InfoIcon } from '@phosphor-icons/react';
import {
  Checkbox,
  ComboBox,
  Select,
  TextField,
  Tooltip,
  cn,
  type SelectOption,
} from '@skupreme/shared/ui';

export type LabeledFieldProps = {
  label: ReactNode;
  /** Shown behind an info mark next to the label. */
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  className?: string;
  isRequired?: boolean;
};

/** Label above its control, with the validation message under it. */
export function LabeledField({
  label,
  hint,
  error,
  children,
  className,
  isRequired,
}: LabeledFieldProps) {
  return (
    <div className={cn('sk:flex sk:min-w-0 sk:flex-col sk:gap-1', className)}>
      <span className="sk:flex sk:items-center sk:gap-1 sk:text-xs sk:text-fg-tertiary">
        {label}
        {isRequired ? <span className="sk:text-danger">*</span> : null}
        {hint ? (
          <Tooltip content={hint}>
            <InfoIcon size={12} />
          </Tooltip>
        ) : null}
      </span>
      {children}
      {error ? (
        <span className="sk:text-xs sk:text-danger">{error}</span>
      ) : null}
    </div>
  );
}

type ControlledProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  hint?: ReactNode;
  error?: string;
  className?: string;
  isRequired?: boolean;
};

export type SelectFieldProps<T extends FieldValues> = ControlledProps<T> & {
  options: SelectOption[];
  placeholder?: string;
  isDisabled?: boolean;
  /** Runs after the form value is set, for the cascades between the selects. */
  onSelect?: (value: string) => void;
};

export function SelectField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  error,
  className,
  options,
  placeholder,
  isDisabled,
  onSelect,
  isRequired,
}: SelectFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <LabeledField
          label={label}
          hint={hint}
          error={error}
          className={className}
          isRequired={isRequired}
        >
          <Select
            aria-label={label}
            options={options}
            value={typeof field.value === 'string' ? field.value : ''}
            placeholder={placeholder}
            isDisabled={isDisabled}
            onChange={value => {
              field.onChange(value);
              onSelect?.(value);
            }}
          />
        </LabeledField>
      )}
    />
  );
}

export type ComboBoxFieldProps<T extends FieldValues> = ControlledProps<T> & {
  options: SelectOption[];
  placeholder?: string;
  isDisabled?: boolean;
};

/** A `SelectField` that also accepts a carrier we were never told about. */
export function ComboBoxField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  error,
  className,
  options,
  placeholder,
  isDisabled,
}: ComboBoxFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <LabeledField
          label={label}
          hint={hint}
          error={error}
          className={className}
        >
          <ComboBox
            aria-label={label}
            options={options}
            value={typeof field.value === 'string' ? field.value : ''}
            placeholder={placeholder}
            isDisabled={isDisabled}
            onChange={field.onChange}
          />
        </LabeledField>
      )}
    />
  );
}

export type NumberFieldProps<T extends FieldValues> = ControlledProps<T> & {
  placeholder?: string;
};

export function NumberField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  error,
  className,
  placeholder,
}: NumberFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <LabeledField
          label={label}
          hint={hint}
          error={error}
          className={className}
        >
          <TextField
            label={label}
            type="number"
            value={
              field.value === null || field.value === undefined
                ? ''
                : String(field.value)
            }
            placeholder={placeholder}
            onChange={value => field.onChange(value === '' ? null : +value)}
            onBlur={field.onBlur}
          />
        </LabeledField>
      )}
    />
  );
}

export function TextInputField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  error,
  className,
  placeholder,
}: NumberFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <LabeledField
          label={label}
          hint={hint}
          error={error}
          className={className}
        >
          <TextField
            label={label}
            value={typeof field.value === 'string' ? field.value : ''}
            placeholder={placeholder}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        </LabeledField>
      )}
    />
  );
}

export type CheckboxFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  /** Omit for a bare box, which then needs `aria-label` of its own. */
  children?: ReactNode;
  isDisabled?: boolean;
  'aria-label'?: string;
};

export function CheckboxField<T extends FieldValues>({
  control,
  name,
  children,
  isDisabled,
  'aria-label': ariaLabel,
}: CheckboxFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Checkbox
          aria-label={ariaLabel}
          isSelected={!!field.value}
          isDisabled={isDisabled}
          onChange={field.onChange}
        >
          {children}
        </Checkbox>
      )}
    />
  );
}

/** Semantic UI's option shape, which the legacy hooks still hand out. */
export type LegacyOption = { value: string; text: string };

export function toSelectOptions(options: LegacyOption[]): SelectOption[] {
  return (options || [])
    .filter(option => !!option?.value)
    .map(option => ({ value: option.value, label: option.text }));
}
